/* eslint-disable eqeqeq */
import _ from 'lodash'
import { LitElement, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import style from './style/zaptic-select-style'
import { classMap } from 'lit/directives/class-map.js'

export enum Events {
    onSelected = 'on-selected',
}

interface CustomEventPayload {
    [Events.onSelected]: { selected: any }
}

export interface CustomEvents {
    [Events.onSelected]: CustomEvent<CustomEventPayload['on-selected']>
}

type VALUE = string | number

@customElement('zaptic-select')
export class ZapticSelect extends LitElement {
    static styles = style

    @property()
    placeholder: string

    @property({ type: Boolean })
    isInvalid = false

    @property({ type: Array })
    options: Array<{ label: string; value: VALUE }>

    @property()
    selected: VALUE

    @property({ type: Boolean })
    readonly: boolean

    @state()
    private _isDropdownOpen: boolean

    @state()
    private _selectedLabel: string

    connectedCallback() {
        super.connectedCallback()
        addEventListener('click', this.hideDropdown)
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        removeEventListener('click', this.hideDropdown)
    }

    private hideDropdown = (event: Event) => {
        const eventTargets = event.composedPath()
        if (this._isDropdownOpen && !eventTargets.includes(this.renderRoot)) {
            this._isDropdownOpen = false
        }
    }

    private toggleDropdown() {
        if (!this.readonly) {
            this._isDropdownOpen = !this._isDropdownOpen
        }
    }

    private handleOnSelected(value: VALUE) {
        this.dispatchEvent(
            new CustomEvent<CustomEventPayload['on-selected']>(Events.onSelected, {
                detail: { selected: value },
            })
        )
        this._isDropdownOpen = false
    }

    willUpdate() {
        this._selectedLabel = this.options.find(({ value }) => this.selected === value)?.label
    }

    render() {
        //TODO use <select> properly https://stackoverflow.com/a/56354946
        return html`
            <div class="container">
                <zaptic-select-box
                    ?invalid=${this.isInvalid}
                    ?open=${this._isDropdownOpen}
                    ?readonly=${this.readonly}
                    @click=${this.toggleDropdown}
                    aria-haspopup="true"
                    aria-expanded=${this._isDropdownOpen ? 'true' : 'false'}
                    tabIndex="0"
                >
                    <zaptic-ellipsis class=${classMap({ 'is-placeholder': !this._selectedLabel })}>
                        ${this._selectedLabel ?? this.placeholder}
                    </zaptic-ellipsis>
                </zaptic-select-box>

                <div class="${classMap({ options: true, 'is-open': this._isDropdownOpen })}">
                    ${this.options.map(
                        ({ label, value }) =>
                            html`<div
                                @click=${() => this.handleOnSelected(value)}
                                class="${classMap({ option: true, 'is-selected': this.selected == value })}"
                            >
                                <zaptic-ellipsis>${label}</zaptic-ellipsis>
                            </div>`
                    )}
                </div>
            </div>
        `
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'zaptic-select': ZapticSelect
    }
}
